import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";

import type { WerkplekKlantRegel } from "@/lib/werkplek-api";

import { copilootQueryKey } from "../Werkplek/Copiloot/copilootApi";

import { v6Api } from "./api";
import { Icoon } from "./Icoon";
import { foutTekst } from "./Midden/vak";
import { v6Sleutels } from "./sleutels";

/**
 * Zoek- en koppelvlak "Klant koppelen" (controle 72, sessie 73 blok 3 en 4).
 * Eén vlak voor de belstand (stijl "bel", op de iPhone een vlak met kruisje)
 * en het klantvlak rechts (stijl "vlak", ook bij een afgeronde zaak). JP
 * zoekt op naam, postcode of nummer; een klik koppelt nog niet: eerst één
 * bevestigingsregel met "Koppelen" en "Terug". Route: POST
 * /api/werkplek/v6/zaken/{id}/klant.
 */
export interface Gekoppeld {
  zaakId: string;
  klantId: string;
  naam: string;
}

/** Zoeken pas vanaf twee tekens, even wachten tot JP stopt met typen. */
const MIN_ZOEK = 2;

function useVertraagd(waarde: string, ms = 250): string {
  const [vertraagd, setVertraagd] = useState(waarde);
  useEffect(() => {
    const t = window.setTimeout(() => setVertraagd(waarde), ms);
    return () => window.clearTimeout(t);
  }, [waarde, ms]);
  return vertraagd;
}

function klantOnder(r: WerkplekKlantRegel): string {
  return [
    r.woonplaats,
    r.aantal_orders > 0
      ? `${r.aantal_orders} ${r.aantal_orders === 1 ? "bestelling" : "bestellingen"}`
      : "nog geen bestelling",
    r.dieren && r.dieren.length > 0 ? r.dieren.join(", ") : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function KlantKoppelVlak({
  zaakId,
  stijl,
  onSluit,
  onGekoppeld,
}: {
  zaakId: string;
  stijl: "bel" | "vlak";
  onSluit: () => void;
  onGekoppeld: (g: Gekoppeld) => void;
}) {
  const p = stijl === "bel" ? "belkoppel" : "rk-koppel";
  const stil = stijl === "bel" ? "uitleg" : "stil";
  const queryClient = useQueryClient();
  const [zoek, setZoek] = useState("");
  const [gekozen, setGekozen] = useState<WerkplekKlantRegel | null>(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const vraag = useVertraagd(zoek.trim());
  const klanten = useQuery({
    queryKey: ["v6", "koppelvlak", "klanten", vraag],
    queryFn: () => v6Api.zoekKlanten(vraag),
    enabled: vraag.length >= MIN_ZOEK,
    staleTime: 30_000,
  });
  const rijen = klanten.data?.klanten ?? [];

  useEffect(() => {
    const toets = (e: KeyboardEvent) => {
      if (e.key === "Escape") onSluit();
    };
    document.addEventListener("keydown", toets);
    return () => document.removeEventListener("keydown", toets);
  }, [onSluit]);

  const koppel = async (klant: WerkplekKlantRegel) => {
    setBezig(true);
    setFout(null);
    try {
      await v6Api.zaakKlantKoppelen(zaakId, klant.id);
      onGekoppeld({ zaakId, klantId: klant.id, naam: klant.naam });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: v6Sleutels.zaak(zaakId) }),
        queryClient.invalidateQueries({ queryKey: v6Sleutels.lijst }),
        queryClient.invalidateQueries({ queryKey: ["v6", "koppeling", zaakId] }),
        // De copiloot van de zaak meldde "geen klant gekoppeld": opnieuw ophalen.
        queryClient.invalidateQueries({ queryKey: copilootQueryKey({ soort: "zaak", id: zaakId }) }),
      ]);
    } catch (e) {
      setFout(foutTekst(e, "Koppelen lukte niet. Probeer het opnieuw."));
      setBezig(false);
    }
  };

  let lijst: ReactNode;
  if (gekozen) {
    const wie = [gekozen.naam, gekozen.woonplaats].filter(Boolean).join(", ");
    lijst = (
      <div className={`${p}-bevestig`}>
        <span>Koppelen aan {wie}</span>
        <span className="knoppen">
          <button
            type="button"
            className="v6-knop acc"
            disabled={bezig}
            onClick={() => void koppel(gekozen)}
          >
            Koppelen
          </button>
          <button
            type="button"
            className="v6-knop"
            disabled={bezig}
            onClick={() => {
              setGekozen(null);
              setFout(null);
            }}
          >
            Terug
          </button>
        </span>
      </div>
    );
  } else if (vraag.length < MIN_ZOEK) {
    lijst = null;
  } else if (klanten.isPending) {
    lijst = <p className={stil}>Zoeken</p>;
  } else if (klanten.isError) {
    lijst = (
      <p className="v6-fout" role="alert">
        Zoeken lukte niet. Probeer het opnieuw.
      </p>
    );
  } else if (rijen.length === 0) {
    lijst = <p className={stil}>Geen klant gevonden</p>;
  } else {
    lijst = (
      <ul className={`${p}-lijst`} aria-label="Gevonden klanten">
        {rijen.slice(0, 8).map((r) => (
          <li key={r.id}>
            <button type="button" onClick={() => setGekozen(r)}>
              <b>{r.naam}</b>
              <small>{klantOnder(r)}</small>
            </button>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div
      className={`${p} open`}
      role={stijl === "bel" ? "dialog" : "group"}
      aria-label="Klant koppelen"
    >
      <div className={`${p}-kop`}>
        <b>Klant koppelen</b>
        <button type="button" className={`${p}-dicht`} aria-label="Sluiten" onClick={onSluit}>
          <Icoon naam="x" klein />
        </button>
      </div>
      {!gekozen && (
        <label className="v6-zoek">
          <Icoon naam="zoek" klein />
          <input
            type="search"
            autoFocus
            placeholder="Naam, postcode of nummer"
            aria-label="Zoek een klant om te koppelen"
            value={zoek}
            onChange={(e) => setZoek(e.target.value)}
          />
        </label>
      )}
      {lijst}
      {fout && (
        <p className="v6-fout" role="alert">
          {fout}
        </p>
      )}
    </div>
  );
}
